import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { BellRing, Camera, ChevronRight, KeyRound, LogOut, Users } from "lucide-react";
import { AvatarPreset, Button, SmartImage } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { Locale } from "@ec2/i18n";
import { locales } from "@ec2/i18n";
import { useTRPC } from "../lib/api";
import { appConfig } from "../lib/config";
import { contactUrl } from "../lib/links";
import { generalError, type LocalizedError } from "../lib/errors";
import { getThemePreference, setThemePreference, type ThemePreference } from "../lib/theme";
import { storeLeftEvent } from "../lib/leftEvent";
import { isStandalone, subscribeToPush } from "../lib/pwa";
import { postWithProgress } from "../lib/upload";
import { useActive } from "../state/hooks";
import { useSession } from "../state/session";
import { LeaveEventSheet } from "../components/LeaveEventSheet";
import { PasskeyPrompt } from "../components/PasskeyPrompt";
import { Shell } from "../components/Shell";
import { StyledQr } from "../components/StyledQr";
import { ErrorLine, LoadingLine } from "../components/common";

/**
 * Profile (brief §6): the badge card with the personal contact QR is the hero;
 * below it: passkey save, interests + LinkedIn, settings (language/theme/
 * notifications), contacts, tour replay.
 */
export function ProfileScreen() {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const { auth, locale, setLocale } = useSession();
  const { active, user, me, isLoading } = useActive();
  const queryClient = useQueryClient();
  const updateProfile = useMutation(trpc.participant.updateProfile.mutationOptions());
  const avatarUploadUrl = useMutation(trpc.participant.avatarUploadUrl.mutationOptions());
  const confirmAvatar = useMutation(trpc.participant.confirmAvatar.mutationOptions());
  const subscribePush = useMutation(trpc.push.subscribe.mutationOptions());
  const testPush = useMutation(trpc.push.test.mutationOptions());
  const leaveEvent = useMutation(trpc.participant.leaveEvent.mutationOptions());

  const [interestInput, setInterestInput] = useState("");
  const [linkedin, setLinkedin] = useState<string | null>(null);
  const [theme, setTheme] = useState<ThemePreference>(() => getThemePreference());
  const [error, setError] = useState<LocalizedError | null>(null);
  const [saved, setSaved] = useState(false);
  const [passkeyState, setPasskeyState] = useState<"idle" | "busy" | "done" | "failed">("idle");
  const [pushState, setPushState] = useState<"idle" | "busy" | "on">("idle");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [passkeyPromptOpen, setPasskeyPromptOpen] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  if (isLoading || !active || !user) {
    return (
      <Shell active="profile">
        <LoadingLine />
      </Shell>
    );
  }

  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const saveProfile = async (patch: { interests?: string[]; linkedinUrl?: string | null; locale?: Locale }) => {
    setError(null);
    try {
      await updateProfile.mutateAsync({ eventId: active.eventId, ...patch });
      await me.refetch();
      flash();
    } catch (err) {
      setError(generalError(err));
    }
  };

  const addInterest = () => {
    const value = interestInput.trim();
    if (!value) return;
    setInterestInput("");
    void saveProfile({ interests: [...active.interests, value].slice(0, 20) });
  };

  const removeInterest = (interest: string) => {
    void saveProfile({ interests: active.interests.filter((entry) => entry !== interest) });
  };

  const switchLocale = (next: Locale) => {
    setLocale(next);
    void saveProfile({ locale: next });
  };

  const switchTheme = (next: ThemePreference) => {
    setTheme(next);
    setThemePreference(next);
  };

  const addPasskey = async () => {
    setPasskeyState("busy");
    try {
      const result = await auth.passkey.addPasskey();
      setPasskeyState(result?.error ? "failed" : "done");
      void me.refetch();
    } catch {
      setPasskeyState("failed");
    }
  };

  const enablePush = async () => {
    if (!appConfig.vapidPublicKey) return;
    setPushState("busy");
    try {
      const subscription = await subscribeToPush(appConfig.vapidPublicKey);
      if (!subscription) {
        setPushState("idle");
        return;
      }
      await subscribePush.mutateAsync({ eventId: active.eventId, ...subscription });
      setPushState("on");
    } catch (err) {
      setError(generalError(err));
      setPushState("idle");
    }
  };

  const confirmLeave = async () => {
    setError(null);
    try {
      const eventName = active.event.name;
      await leaveEvent.mutateAsync({ eventId: active.eventId });
      // Everything cached belongs to the now-deleted participation.
      queryClient.clear();
      // One-shot flash via sessionStorage — never a spoofable URL param.
      storeLeftEvent(eventName);
      void navigate({ to: "/join" });
    } catch (err) {
      setLeaveOpen(false);
      setError(generalError(err));
    }
  };

  const uploadAvatar = async (file: File | undefined) => {
    if (!file) return;
    setAvatarBusy(true);
    setError(null);
    try {
      const contentType = file.type || "image/jpeg";
      const { url, fields, key } = await avatarUploadUrl.mutateAsync({ eventId: active.eventId, contentType });
      await postWithProgress(url, fields, file);
      // The PUT alone changes nothing server-side — confirm to persist the key.
      await confirmAvatar.mutateAsync({ key });
      await queryClient.invalidateQueries();
      await me.refetch();
    } catch (err) {
      setError(generalError(err));
    } finally {
      setAvatarBusy(false);
    }
  };

  const rowClass =
    "flex w-full items-center gap-3 rounded-(--radius-card) border border-border bg-surface px-4 py-3.5 text-left " +
    "focus-visible:outline-2 focus-visible:outline-primary";

  return (
    <Shell active="profile">
      <header className="px-4 pb-3 pt-4">
        <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">{t("ec2.profile.title")}</h1>
      </header>

      <div className="flex flex-col gap-4 px-4 pb-6" data-testid="ec-profile">
        {/* Badge card — the personal QR is the hero. */}
        <section className="flex flex-col items-center gap-3 rounded-(--radius-card) border border-bucks/40 bg-surface-raised p-5">
          <div className="relative">
            {active.avatarKey ? (
              <SmartImage
                base={appConfig.mediaBase}
                mediaKey={active.avatarKey}
                alt={t("ec2.a11y.avatar", { name: active.displayName })}
                width={72}
                height={72}
                maxVariant="thumb"
                className="size-[72px] rounded-full object-cover"
              />
            ) : (
              <AvatarPreset preset={active.avatarPresetId} size={72} label={t("ec2.a11y.avatar", { name: active.displayName })} />
            )}
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              aria-label={t("ec2.profile.avatar.change")}
              data-testid="ec-avatar-change"
              disabled={avatarBusy}
              className="absolute -bottom-1 -right-1 flex size-8 items-center justify-center rounded-full bg-primary text-primary-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <Camera size={16} aria-hidden />
            </button>
          </div>
          {avatarBusy && (
            <p role="status" className="text-[13px] font-medium text-fg-muted">
              {t("ec2.profile.avatar.uploading")}
            </p>
          )}
          <h2 className="font-display text-[21px] font-[650] text-fg" data-testid="ec-profile-name">
            {active.displayName}
          </h2>
          <StyledQr value={contactUrl(active.code)} size={180} eyes="primary" />
          <p className="text-center text-[13px] font-medium text-fg-secondary">{t("ec2.profile.getScanned")}</p>
        </section>

        {saved && (
          <p role="status" className="text-[13px] font-medium text-success">
            {t("ec2.profile.saved")}
          </p>
        )}
        <ErrorLine error={error} />

        {/* Passkey (until Rung 1). */}
        {user.kind === "anonymous" && !user.hasPasskey && passkeyState !== "done" && (
          <button type="button" onClick={() => void addPasskey()} disabled={passkeyState === "busy"} className={rowClass} data-testid="ec-passkey-row">
            <span className="flex size-10 items-center justify-center rounded-(--radius-chip) bg-primary/15 text-primary">
              <KeyRound size={20} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold text-fg">{t("ec2.passkey.row.title")}</span>
              <span className="block text-[13px] font-medium text-fg-secondary">
                {passkeyState === "failed" ? t("ec2.passkey.failed") : t("ec2.passkey.row.body")}
              </span>
            </span>
            <ChevronRight size={20} className="text-fg-muted" aria-hidden />
          </button>
        )}
        {(passkeyState === "done" || (user.kind === "anonymous" && user.hasPasskey)) && (
          <p role="status" className="text-[13px] font-medium text-success" data-testid="ec-passkey-saved">
            {t("ec2.passkey.done")}
          </p>
        )}

        {/* Interests + LinkedIn */}
        <section className="flex flex-col gap-3 rounded-(--radius-card) border border-border bg-surface p-4">
          <h3 className="text-[17px] font-semibold text-fg">{t("ec2.profile.interests")}</h3>
          <div className="flex flex-wrap gap-2">
            {active.interests.map((interest) => (
              <button
                key={interest}
                type="button"
                onClick={() => removeInterest(interest)}
                data-testid="ec-interest-chip"
                className="rounded-(--radius-chip) bg-accent/15 px-3 py-1.5 text-[13px] font-medium text-accent focus-visible:outline-2 focus-visible:outline-primary"
              >
                {interest} ×
              </button>
            ))}
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              addInterest();
            }}
          >
            <input
              value={interestInput}
              onChange={(e) => setInterestInput(e.target.value)}
              placeholder={t("ec2.profile.interests.placeholder")}
              data-testid="ec-interest-input"
              className="min-w-0 flex-1 rounded-(--radius-chip) border border-border bg-bg px-3 py-2 text-base text-fg focus-visible:outline-2 focus-visible:outline-primary"
            />
            <Button type="submit" variant="ghost" className="px-4 py-2 text-sm">
              {t("ec2.profile.interests.add")}
            </Button>
          </form>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-fg-secondary">{t("ec2.profile.linkedin")}</span>
            <input
              value={linkedin ?? active.linkedinUrl ?? ""}
              onChange={(e) => setLinkedin(e.target.value)}
              onBlur={() => {
                if (linkedin !== null && linkedin !== (active.linkedinUrl ?? "")) {
                  void saveProfile({ linkedinUrl: linkedin.trim() === "" ? null : linkedin.trim() });
                }
              }}
              inputMode="url"
              data-testid="ec-linkedin-input"
              className="rounded-(--radius-chip) border border-border bg-bg px-3 py-2 text-base text-fg focus-visible:outline-2 focus-visible:outline-primary"
            />
          </label>
        </section>

        {/* Settings */}
        <section className="flex flex-col gap-3 rounded-(--radius-card) border border-border bg-surface p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-base text-fg">{t("ec2.profile.language")}</span>
            <div className="flex gap-2" role="group" aria-label={t("ec2.profile.language")}>
              {locales.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  onClick={() => switchLocale(entry)}
                  aria-pressed={locale === entry}
                  data-testid={`ec-locale-${entry}`}
                  className={
                    `rounded-(--radius-chip) px-3 py-1.5 text-[13px] font-semibold uppercase focus-visible:outline-2 focus-visible:outline-primary ` +
                    (locale === entry ? "bg-primary text-primary-fg" : "bg-surface-overlay text-fg-secondary")
                  }
                >
                  {entry}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-base text-fg">{t("ec2.profile.theme")}</span>
            <div className="flex gap-2" role="group" aria-label={t("ec2.profile.theme")}>
              {(["dark", "light", "system"] as const).map((entry) => (
                <button
                  key={entry}
                  type="button"
                  onClick={() => switchTheme(entry)}
                  aria-pressed={theme === entry}
                  data-testid={`ec-theme-${entry}`}
                  className={
                    `rounded-(--radius-chip) px-3 py-1.5 text-[13px] font-semibold focus-visible:outline-2 focus-visible:outline-primary ` +
                    (theme === entry ? "bg-primary text-primary-fg" : "bg-surface-overlay text-fg-secondary")
                  }
                >
                  {t(`ec2.profile.theme.${entry}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Push opt-in (spec §5: only as installed PWA). */}
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-base text-fg">
              <BellRing size={18} aria-hidden className="text-fg-secondary" />
              {t("ec2.profile.notifications")}
            </span>
            {!isStandalone() || !appConfig.vapidPublicKey ? (
              <span className="text-right text-[13px] font-medium text-fg-muted">{t("ec2.profile.push.standaloneOnly")}</span>
            ) : pushState === "on" ? (
              <Button
                variant="ghost"
                className="px-4 py-2 text-sm"
                disabled={testPush.isPending}
                onClick={() => void testPush.mutateAsync()}
                data-testid="ec-push-test"
              >
                {t("ec2.profile.push.test")}
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="px-4 py-2 text-sm"
                disabled={pushState === "busy"}
                onClick={() => void enablePush()}
                data-testid="ec-push-enable"
              >
                {t("ec2.profile.push.enable")}
              </Button>
            )}
          </div>
        </section>

        {/* Navigation rows */}
        <button type="button" onClick={() => void navigate({ to: "/contacts" })} className={rowClass} data-testid="ec-row-contacts">
          <Users size={20} className="text-fg-secondary" aria-hidden />
          <span className="flex-1 text-base font-semibold text-fg">{t("ec2.profile.contacts")}</span>
          <ChevronRight size={20} className="text-fg-muted" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => void navigate({ to: "/quests", search: { tour: 1 } })}
          className={rowClass}
          data-testid="ec-row-tour"
        >
          <span className="flex-1 text-base font-semibold text-fg">{t("ec2.profile.howToPlay")}</span>
          <ChevronRight size={20} className="text-fg-muted" aria-hidden />
        </button>

        {/* Danger zone — leaving permanently deletes all event progress. */}
        <section className="mt-2 border-t border-border pt-4" data-testid="ec-danger-zone">
          <button
            type="button"
            onClick={() => setLeaveOpen(true)}
            className={`${rowClass} border-danger/40`}
            data-testid="ec-leave-row"
          >
            <LogOut size={20} className="text-danger" aria-hidden />
            <span className="flex-1 text-base font-semibold text-danger">{t("ec2.profile.leave.row")}</span>
            <ChevronRight size={20} className="text-fg-muted" aria-hidden />
          </button>
        </section>
      </div>

      {leaveOpen && (
        <LeaveEventSheet
          busy={leaveEvent.isPending}
          onConfirm={() => void confirmLeave()}
          onCancel={() => setLeaveOpen(false)}
          offerPasskey={!user.hasPasskey}
          onPasskeyInstead={() => {
            setLeaveOpen(false);
            setPasskeyPromptOpen(true);
          }}
        />
      )}
      {passkeyPromptOpen && <PasskeyPrompt onDone={() => setPasskeyPromptOpen(false)} />}

      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label={t("ec2.profile.avatar.change")}
        data-testid="ec-avatar-input"
        onChange={(e) => void uploadAvatar(e.target.files?.[0])}
      />
    </Shell>
  );
}
